import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;
// Token refresh before expiry (5 minutes before)
const REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('pt_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('pt_refresh_token'));
  const [loading, setLoading] = useState(true);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  
  const activityTimeoutRef = useRef(null);
  const tokenRefreshTimeoutRef = useRef(null);

  // Reset activity timeout on user activity
  const resetActivityTimeout = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    if (user) {
      activityTimeoutRef.current = setTimeout(() => {
        console.log('Session expired due to inactivity');
        logout(true);
      }, SESSION_TIMEOUT);
    }
  }, [user]);

  // Setup activity listeners
  useEffect(() => {
    if (user) {
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, resetActivityTimeout);
      });
      
      resetActivityTimeout();
      
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, resetActivityTimeout);
        });
        if (activityTimeoutRef.current) {
          clearTimeout(activityTimeoutRef.current);
        }
      };
    }
  }, [user, resetActivityTimeout]);

  // Token refresh logic
  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) return false;
    
    try {
      const response = await axios.post(`${API_URL}/auth/refresh`, {
        refresh_token: refreshToken
      });
      
      const { access_token, refresh_token: newRefreshToken, expires_in } = response.data;
      
      localStorage.setItem('pt_token', access_token);
      localStorage.setItem('pt_refresh_token', newRefreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setRefreshToken(newRefreshToken);
      
      // Schedule next refresh
      const refreshTime = (expires_in * 1000) - REFRESH_BEFORE_EXPIRY;
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current);
      }
      tokenRefreshTimeoutRef.current = setTimeout(refreshAccessToken, refreshTime);
      
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout(true);
      return false;
    }
  }, [refreshToken]);

  // Setup axios interceptor for 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry && refreshToken) {
          originalRequest._retry = true;
          
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            originalRequest.headers['Authorization'] = `Bearer ${localStorage.getItem('pt_token')}`;
            return axios(originalRequest);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshToken, refreshAccessToken]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      
      // Schedule token refresh if we have expiry info
      const expiresIn = localStorage.getItem('pt_expires_in');
      if (expiresIn) {
        const refreshTime = (parseInt(expiresIn) * 1000) - REFRESH_BEFORE_EXPIRY;
        tokenRefreshTimeoutRef.current = setTimeout(refreshAccessToken, Math.max(refreshTime, 60000));
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return {
        requires_2fa: true,
        pending_token: response.data.pending_token,
        message: response.data.message
      };
    }
    
    // Normal login flow
    const { access_token, refresh_token, expires_in, user: userData } = response.data;
    
    localStorage.setItem('pt_token', access_token);
    localStorage.setItem('pt_refresh_token', refresh_token);
    localStorage.setItem('pt_expires_in', expires_in.toString());
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setRefreshToken(refresh_token);
    setUser(userData);
    
    // Schedule token refresh
    const refreshTime = (expires_in * 1000) - REFRESH_BEFORE_EXPIRY;
    tokenRefreshTimeoutRef.current = setTimeout(refreshAccessToken, refreshTime);
    
    return userData;
  };

  const verify2FA = async (email, code, pendingToken) => {
    const response = await axios.post(`${API_URL}/auth/verify-2fa`, {
      email,
      code,
      pending_token: pendingToken
    });
    
    const { access_token, refresh_token, expires_in, user: userData } = response.data;
    
    localStorage.setItem('pt_token', access_token);
    localStorage.setItem('pt_refresh_token', refresh_token);
    localStorage.setItem('pt_expires_in', expires_in.toString());
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setRefreshToken(refresh_token);
    setUser(userData);
    
    // Schedule token refresh
    const refreshTime = (expires_in * 1000) - REFRESH_BEFORE_EXPIRY;
    tokenRefreshTimeoutRef.current = setTimeout(refreshAccessToken, refreshTime);
    
    return userData;
  };

  const resend2FACode = async (pendingToken) => {
    await axios.post(`${API_URL}/auth/resend-2fa?pending_token=${encodeURIComponent(pendingToken)}`);
  };

  const logout = async (silent = false) => {
    // Call logout endpoint to invalidate session
    if (!silent && token) {
      try {
        await axios.post(`${API_URL}/auth/logout`);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    localStorage.removeItem('pt_token');
    localStorage.removeItem('pt_refresh_token');
    localStorage.removeItem('pt_expires_in');
    delete axios.defaults.headers.common['Authorization'];
    
    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current);
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, isAdmin, refreshAccessToken, verify2FA, resend2FACode }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
